import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type {
	ShiftClickRange,
	UseTrackSelection,
	UseTrackSelectionParams,
} from './TrackList.types'
import {
	createShiftRangeAt,
	extendShiftRangeTo,
	mergeShiftRangeIntoSelection,
} from './TrackList.utils'

/**
 * Track-list multi-select with Finder/File-Explorer style checkbox interactions:
 * plain click toggles a track, shift-click selects a range, and
 * `handleSelectAll` / `handleClearAll` cover bulk actions. Owns the selection
 * and the shift-click range (`shiftRangeRef`); range maths lives in
 * `TrackList.utils`. The selection is cleared when the track list changes
 * (e.g. re-ordering).
 */
export const useTrackSelection = ({
	tracks,
}: UseTrackSelectionParams): UseTrackSelection => {
	const [selectedTracks, setSelectedTracks] = useState<Track[]>([])

	const tracksSelectedRef = useRef(selectedTracks)
	tracksSelectedRef.current = selectedTracks

	/**
	 * Remembers the current shift-click range, or null when none is active.
	 * On the next shift-click, this is the "before" range so tracks that drop out
	 * of it can be deselected.
	 */
	const shiftRangeRef = useRef<ShiftClickRange | null>(null)

	const trackIdsSignature = useMemo(
		() => tracks.map(({ id }) => id).join(','),
		[tracks]
	)

	/** Clears the selection and shift range when the track list changes. */
	useEffect(() => {
		shiftRangeRef.current = null
		setSelectedTracks([])
	}, [trackIdsSignature])

	/**
	 * Fast id lookups for checkbox `checked` state. Avoids scanning
	 * `selectedTracks` with `.some()` on every row render.
	 */
	const selectedTrackIds = useMemo(
		() => new Set(selectedTracks.map(({ id }) => id)),
		[selectedTracks]
	)

	const clearSelection = useCallback((): void => {
		shiftRangeRef.current = null
		setSelectedTracks([])
	}, [])

	const handleSelectAll = useCallback((): void => {
		shiftRangeRef.current = null
		setSelectedTracks(tracks)
	}, [tracks])

	/** Updates the selection via `mergeShiftRangeIntoSelection`, then stores `nextShiftRange`. */
	const applyShiftRangeChange = useCallback(
		(
			previousShiftRange: ShiftClickRange | null,
			nextShiftRange: ShiftClickRange
		): void => {
			setSelectedTracks((current) =>
				mergeShiftRangeIntoSelection(
					current,
					tracks,
					previousShiftRange,
					nextShiftRange
				)
			)
			shiftRangeRef.current = nextShiftRange
		},
		[tracks]
	)

	const toggleTrack = useCallback((track: Track, index: number): void => {
		const isSelected = tracksSelectedRef.current.some(
			({ id }) => id === track.id
		)

		if (isSelected) {
			// Clear the shift range when the anchored row is deselected, so a later
			// shift-click cannot re-select that track.
			if (shiftRangeRef.current?.anchorIndex === index) {
				shiftRangeRef.current = null
			}

			setSelectedTracks((current) =>
				current.filter(({ id }) => id !== track.id)
			)
			return
		}

		shiftRangeRef.current = createShiftRangeAt(index)
		setSelectedTracks((current) => [...current, track])
	}, [])

	const handleTrackSelected = useCallback(
		(
			event: React.ChangeEvent<HTMLInputElement>,
			track: Track,
			index: number
		): void => {
			const { shiftKey } = event.nativeEvent as MouseEvent

			if (shiftKey) {
				const previousShiftRange = shiftRangeRef.current
				// Empty selection anchors on row 0; otherwise anchors on the clicked row.
				const currentShiftRange =
					shiftRangeRef.current ??
					createShiftRangeAt(tracksSelectedRef.current.length === 0 ? 0 : index)

				applyShiftRangeChange(
					previousShiftRange,
					extendShiftRangeTo(currentShiftRange, index)
				)
				return
			}

			event.target.blur()

			toggleTrack(track, index)
		},
		[applyShiftRangeChange, toggleTrack]
	)

	return {
		selectedTracks,
		selectedTrackIds,
		handleSelectAll,
		handleClearAll: clearSelection,
		handleTrackSelected,
	}
}
